import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { fadeInUp } from '../../lib/animations';

interface StatCardProps {
  value: string;
  label: string;
  description?: string;
  accentColor?: 'blue' | 'purple';
  index?: number;
}

function useCountUp(target: number, inView: boolean, duration = 1.6) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const steps = 60;
    const interval = (duration / steps) * 1000;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(target * eased));
      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return display;
}

export default function StatCard({
  value,
  label,
  description,
  accentColor = 'blue',
  index = 0,
}: StatCardProps) {
  const { ref, controls, inView } = useScrollReveal(0.3);
  const isBlue = accentColor === 'blue';
  const numberColor = isBlue ? '#4A9FFF' : '#A78BFA';

  // Parse numeric part and suffix (e.g. "50+" → 50, "+")
  const match = value.match(/^(\d+)(.*)/);
  const numericTarget = match ? parseInt(match[1]) : null;
  const suffix = match ? match[2] : '';

  const counted = useCountUp(numericTarget ?? 0, inView);

  // For ranges like "3–7" don't animate
  const displayValue = numericTarget !== null ? `${counted}${suffix}` : value;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      custom={index * 0.1}
      className="flex flex-col items-center md:items-start text-center md:text-left px-6 md:px-10"
    >
      <div
        className="font-extrabold leading-none mb-5 md:mb-3"
        style={{
          fontSize: 'clamp(2.25rem, 9vw, 6.875rem)',
          color: numberColor,
          letterSpacing: '-0.03em',
        }}
      >
        {displayValue}
      </div>
      <div className="text-white/40 text-2xs font-medium tracking-[0.15em] uppercase leading-snug max-w-[160px]">
        {label}
      </div>
      {description && (
        <div className="text-white/30 text-xs mt-1 max-w-[150px] leading-relaxed">{description}</div>
      )}
    </motion.div>
  );
}
